<script setup lang="ts">
/**
 * Компонент управления участниками группы аудита.
 *
 * Назначение:
 * - Управление списком участников (сотрудников) в группе аудита.
 * - Добавление и удаление участников.
 *
 * Особенности:
 * - Интеграция с Pinia (useAuditGroupStore).
 * - Использование модального окна выбора сотрудника.
 * - В качестве ID участника используется ID сотрудника из БД.
 */

import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { getFullName } from '@/utils/formatters'
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'

import ParticipantFormRow from '@/components/adverseEvents/ParticipantFormRow.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'

import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'

/**
 * Пропсы компонента.
 */
const props = defineProps<{
  disabled?: boolean
}>()
const auditGroupStore = useAuditGroupStore()
const apiStore = useApiStore()

/**
 * Информация о текущем размере экрана.
 */
const { xs } = useDisplay()

/**
 * Реактивные ссылки на состояние текущей группы аудита.
 */
const {
  currentGroup,
} = storeToRefs(auditGroupStore)

const employeeModalRef = ref()

/**
 * Идентификатор выбранного сотрудника (оставляем для обратной совместимости).
 */
const selectedEmployeeId = ref<string>('')

/**
 * Список выбранных участников группы в формате для модального окна.
 */
const currentGroupSelectedEmployees = computed(() => {
  return currentGroup.value.group_assignments.map(a => ({
    id: a.employee.id,
    full_name: a.employee.full_name,
  } as IEmployeeTreeItem))
})

/**
 * Обработчик множественного выбора сотрудников из модального окна.
 */
const onMultipleEmployeesSelected = (employees: IEmployeeTreeItem[]) => {
  const existingAssignments = [...currentGroup.value.group_assignments]

  currentGroup.value.group_assignments = employees.map(emp => {
    const existing = existingAssignments.find(a => a.employee.id === emp.id)
    return {
      employee: {
        id: emp.id,
        full_name: emp.full_name,
      },
      points: existing ? existing.points : [],
    }
  })
}

/**
 * Открывает модальное окно выбора сотрудников.
 */
const openEmployeeSelection = () => {
  employeeModalRef.value?.open()
}

/**
 * Удаление участника из списка по его ID.
 */
const deleteParticipant = (id: string): void => {
  if (!id) return

  const index = currentGroup.value.group_assignments.findIndex(
    participant => participant.employee.id === id,
  )
  if (index === -1) return

  currentGroup.value.group_assignments.splice(index, 1)
}

/**
 * Текст кнопки действия.
 */
const actionButtonText = 'Выбрать аудиторов'
</script>

<template>
  <div class="d-flex flex-column gap-4 w-100">
    <div class="d-flex align-center">
      <VBtn
        :class="{ 'flex-grow-1': xs }"
        variant="outlined"
        prepend-icon="ri-user-add-line"
        class="rounded-te-0 rounded-be-0"
        :disabled="props.disabled"
        @click="openEmployeeSelection"
      >
        {{ actionButtonText }}
      </VBtn>
      <VIconBtn
        color="primary"
        class="rounded-ts-0 rounded-ds-0 rounded-te rounded-be"
        icon="ri-arrow-down-s-line"
        variant="outlined"
        :rounded="false"
        size="38"
        :disabled="props.disabled"
        @click="openEmployeeSelection"
      />
    </div>

    <ParticipantFormRow
      v-for="(auditor, index) of currentGroup.group_assignments"
      :id="auditor.employee.id"
      :key="auditor.employee.id"
      :index="index + 1"
      :full_name="auditor.employee.full_name || getFullName(auditor.employee)"
      :employee_position_name="auditor.employee.position_name ?? ''"
      birth_date=""
      phone_number=""
      :points="auditor.points"
      :show_delete_button="true"
      :is-audit-participant="true"
      @delete="deleteParticipant"
    />
  </div>

  <EmployeeSelectionModal
    ref="employeeModalRef"
    v-model="selectedEmployeeId"
    :employees="apiStore.employees"
    multiple
    :selected-employees="currentGroupSelectedEmployees"
    @select-multiple="onMultipleEmployeesSelected"
  />
</template>
